import { useId, type ReactNode } from "react";

import type { Tier } from "../../api/generated/model";
import classes from "./achievements.module.css";
import art from "./badges.json";

/**
 * Earned in full colour; locked in grey; a locked secret fogged with "?"; new, it develops once
 * (spec web/achievements, 1).
 */
export type BadgeState = "earned" | "locked" | "secret" | "new";

interface Drawing {
  /** The gradient's id, unique on the page. */
  id: string;
  /** The achievement's emoji, or null for a locked secret's "?". */
  glyph: string | null;
  fogged: boolean;
}

function Stops({ colors }: { colors: readonly string[] }) {
  return colors.map((color, at) => (
    <stop key={at} offset={colors.length === 1 ? 0 : at / (colors.length - 1)} stopColor={color} />
  ));
}

function Glyph({ x, y, size, glyph }: { x: number; y: number; size: number; glyph: string | null }) {
  const place = { x, y, fontSize: size, textAnchor: "middle", dominantBaseline: "central" } as const;
  if (glyph !== null) return <text {...place}>{glyph}</text>;
  return (
    <text {...place} fill={art.secretInk} fontWeight={700}>
      ?
    </text>
  );
}

const TICKET =
  "M14,24 H86 A6,6 0 0 1 92,30 V43 A7,7 0 0 0 92,57 V70 A6,6 0 0 1 86,76 H14 A6,6 0 0 1 8,70 V57 A7,7 0 0 0 8,43 V30 A6,6 0 0 1 14,24 Z";
const STAR = Array.from({ length: 10 }, (_, at) => {
  const radius = at % 2 === 1 ? 21 : 45;
  const angle = -Math.PI / 2 + (at * Math.PI) / 5;
  return `${(50 + radius * Math.cos(angle)).toFixed(1)},${(53 + radius * Math.sin(angle)).toFixed(1)}`;
}).join(" ");
const SHIELD = "M50,7 L87,19 V47 C87,72 71,86 50,95 C29,86 13,72 13,47 V19 Z";

/** Extra: an admission ticket. */
function Ticket({ id, glyph, fogged }: Drawing) {
  const tier = art.extra;
  const ink = fogged ? tier.fogInk : tier.ink;
  return (
    <>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <Stops colors={fogged ? tier.fog : tier.fill} />
      </linearGradient>
      <path d={TICKET} fill={`url(#${id})`} stroke={ink} strokeWidth={2.2} />
      <path d="M70,29 V71" stroke={ink} strokeWidth={1.6} strokeDasharray="3 3" />
      <circle cx={80} cy={50} r={4} fill="none" stroke={ink} strokeWidth={1.6} />
      <Glyph x={40} y={51} size={fogged ? 32 : 34} glyph={glyph} />
    </>
  );
}

/** Supporting Role: a bronze film reel. */
function Reel({ id, glyph, fogged }: Drawing) {
  const tier = art["supporting-role"];
  const ink = fogged ? tier.fogInk : tier.ink;
  return (
    <>
      <radialGradient id={id} cx=".35" cy=".3" r=".8">
        <Stops colors={fogged ? tier.fog : tier.fill} />
      </radialGradient>
      <circle cx={50} cy={50} r={41} fill={`url(#${id})`} stroke={ink} strokeWidth={2} />
      {Array.from({ length: 8 }, (_, at) => (
        <circle
          key={at}
          cx={50 + 34.5 * Math.cos((at * Math.PI) / 4)}
          cy={50 + 34.5 * Math.sin((at * Math.PI) / 4)}
          r={2.7}
          fill={fogged ? tier.fogInk : tier.holes}
        />
      ))}
      <circle cx={50} cy={50} r={28} fill={fogged ? tier.fogHub : tier.hub} stroke={fogged ? ink : tier.hubInk} strokeWidth={1.5} />
      <Glyph x={50} y={51} size={fogged ? 30 : 32} glyph={glyph} />
    </>
  );
}

/** Leading Role: a silver star with an indigo centre. */
function Star({ id, glyph, fogged }: Drawing) {
  const tier = art["leading-role"];
  return (
    <>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <Stops colors={fogged ? tier.fog : tier.fill} />
      </linearGradient>
      <polygon points={STAR} fill={`url(#${id})`} stroke={fogged ? tier.fogInk : tier.ink} strokeWidth={1.8} strokeLinejoin="round" />
      <circle cx={50} cy={54} r={17} fill={fogged ? tier.fogCore : tier.core} stroke={fogged ? tier.fogInk : tier.coreInk} strokeWidth={1.5} />
      <Glyph x={50} y={55} size={fogged ? 21 : 22} glyph={glyph} />
    </>
  );
}

/** Director's Cut: a gold shield with a filmstrip band. */
function Shield({ id, glyph, fogged }: Drawing) {
  const tier = art["directors-cut"];
  return (
    <>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <Stops colors={fogged ? tier.fog : tier.fill} />
      </linearGradient>
      <clipPath id={`${id}-clip`}>
        <path d={SHIELD} />
      </clipPath>
      <path d={SHIELD} fill={`url(#${id})`} stroke={fogged ? tier.fogInk : tier.ink} strokeWidth={2} />
      <g clipPath={`url(#${id}-clip)`}>
        <rect x={0} y={25} width={100} height={13} fill={fogged ? tier.fogBand : tier.band} />
        {Array.from({ length: 6 }, (_, at) => (
          <rect key={at} x={22 + at * 10} y={29.5} width={5} height={4} rx={1} fill={fogged ? tier.fogInk : tier.sprocket} />
        ))}
      </g>
      <Glyph x={50} y={64} size={fogged ? 30 : 31} glyph={glyph} />
    </>
  );
}

/** Cult Classic: a holographic cassette whose foil turns slowly. */
function Cassette({ id, glyph, fogged }: Drawing) {
  const tier = art["cult-classic"];
  return (
    <>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <Stops colors={fogged ? tier.fog : tier.fill} />
      </linearGradient>
      <clipPath id={`${id}-clip`}>
        <rect x={9} y={21} width={82} height={58} rx={9} />
      </clipPath>
      <g clipPath={`url(#${id}-clip)`}>
        {fogged ? (
          <rect x={0} y={0} width={100} height={100} fill={`url(#${id})`} />
        ) : (
          <rect className={classes.foil} x={-30} y={-30} width={160} height={160} fill={`url(#${id})`} />
        )}
      </g>
      <rect x={9} y={21} width={82} height={58} rx={9} fill="none" stroke={fogged ? tier.fogInk : tier.ink} strokeWidth={2} />
      <rect x={18} y={28} width={64} height={27} rx={4} fill={fogged ? tier.fogLabel : tier.label} />
      {[36, 64].map((cx) => (
        <g key={cx}>
          <circle cx={cx} cy={66} r={6.5} fill={tier.reel} />
          <circle cx={cx} cy={66} r={2.6} fill={tier.spindle} />
        </g>
      ))}
      <rect x={43} y={63} width={14} height={6} rx={2} fill={tier.reel} opacity={0.75} />
      <Glyph x={50} y={42} size={fogged ? 23 : 25} glyph={glyph} />
    </>
  );
}

const OBJECTS: Record<Tier, (drawing: Drawing) => ReactNode> = {
  extra: Ticket,
  "supporting-role": Reel,
  "leading-role": Star,
  "directors-cut": Shield,
  "cult-classic": Cassette,
};

/** An achievement's badge: its tier's object, its glyph in the middle, drawn for its state. */
export function Badge({ tier, glyph, state, size }: { tier: Tier; glyph: string | null; state: BadgeState; size: number }) {
  const id = `badge-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const Draw = OBJECTS[tier];
  const fogged = state === "secret";
  return (
    <span className={classes.badge} data-state={state} style={{ width: size, height: size }} aria-hidden>
      <svg viewBox="0 0 100 100">
        <Draw id={id} glyph={fogged ? null : glyph} fogged={fogged} />
      </svg>
    </span>
  );
}
